import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl, UntypedFormControl } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NumericTextBoxComponent, NumericTextBoxModule, TextBoxModule } from '@progress/kendo-angular-inputs';
import { ButtonVariant, QPointButtonComponent } from '@qpoint/buttons';
import { QPointIconModule } from '@qpoint/icons';
import { BehaviorSubject, Observable, Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { QPointValidationMessageComponent } from '../validation-message/validation-message.component';
import { QPointInlineEditAdditionDirective } from './inline-edit-addition.directive';

@Component({
    selector: 'qpoint-inline-editing-input',
    templateUrl: './inline-editing-input.component.html',
    styleUrls: ['./inline-editing-input.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        TextBoxModule,
        FormsModule,
        NumericTextBoxModule,
        QPointIconModule,
        QPointValidationMessageComponent,
        QPointButtonComponent,
        TranslateModule
    ]
})
export class QPointInlineEditingInputComponent implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor {

  public controlForValidation = new UntypedFormControl();
  public controlForValidationDisplayActive = false;

  constructor(@Self() @Optional() public control: NgControl) {
    if (this.control) {
      this.control.valueAccessor = this;
    }
  }

  ButtonVariant = ButtonVariant;

  @ViewChild('inputElementReference')
  public inputElement: any;

  @ViewChild('invisibleSuffixElement')
  public invisibleSuffixElement: any;

  actualClass = '';

  public activeInlineEditingInternal$ = new BehaviorSubject<boolean>(false);

  public scrollbarIsVisible = false;

  @Input()
  public infoMessageOnEdit: string;

  @Input()
  public placeholder = '';
  @Input()
  public suffix: string;
  @Input()
  public numericInput = false;

  @Input()
  public readonly;
  @Input()
  public activeInlineEditing: boolean | Observable<boolean> = false;

  @Input()
  public editDisabled = false;

  @Input()
  public loadingIndicator: boolean;
  public readonly$ = new BehaviorSubject(false);

  @Input()
  public isTextArea = false;

  @Output()
  public editModeIsActive = new EventEmitter<boolean>();

  @Output() 
  public touch: EventEmitter<any> = new EventEmitter();

  @ContentChild(QPointInlineEditAdditionDirective, { read: TemplateRef })
  public addition: any;

  /***
   * OnChange and OnTouched PlaceHolders for ngValueAccessor Callback registration
   */
  public onChange;
  public onTouched;

  public isDisabled: boolean;

  public inputFieldValue: string;
  private inputFieldValueChangedSubject: Subject<string> = new Subject<string>();

  private currentValue: string;

  private ngUnsubscribe = new Subject<void>();

  ngOnInit() {
    this.activeInlineEditingInternal$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => this.readonly$.next(value));

    if (this.activeInlineEditing instanceof Observable) {
      this.activeInlineEditing
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(value => {
          this.activeInlineEditingInternal$.next(value);
        });
    } else {
      this.activeInlineEditingInternal$.next(this.activeInlineEditing);
    }

    timer(1).subscribe(() => {
      this.readonly$.subscribe(read => {
        this.actualClass = read ? 'inlineEditDisabled' : '';
      });
    });
  }

  ngAfterViewInit(): void {
    this.checkForScrollbar();

    if (this.readonly) {
      this.readonly$.next(true);
    }

    if (this.suffix && !this.numericInput) {
      timer(1).subscribe(() => {
        this.inputElement.nativeElement.style.setProperty('text-indent',
          this.invisibleSuffixElement.nativeElement.getBoundingClientRect().width + 'px');
      });
    }

    this.controlForValidation.addValidators(this.control?.control?.validator);
  }

  // Should be called whenever something in the view has changed

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // Is used whenever the control receives a touch event

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  public setInputFieldValue(value: string) {
    if (!this.readonly$.getValue()) {
      this.inputFieldValue = value;
    }
  }

  writeValue(obj: any): void {
    this.currentValue = obj;
    this.inputFieldValue = this.currentValue;
  }

  inputFieldValueChange($event: any) {
    this.inputFieldValueChangedSubject.next($event);
    timer(1).subscribe(() => {
      this.updateControlForValidationMessage();
    });
    if (!this.activeInlineEditingInternal$.getValue()) {
      this.onChange($event);
    }
    this.checkForScrollbar();
  }

  public toggleReadOnly() {
    this.checkForScrollbar();
    this.readonly$.next(!this.readonly$.getValue());
    if (!this.readonly$.getValue()) {
      if (this.inputElement instanceof NumericTextBoxComponent) {
        timer(1).subscribe(value => this.inputElement.focus());
      } else {
        this.inputElement.nativeElement.focus();
      }
      this.editModeIsActive.next(true);
    } else {
      this.editModeIsActive.next(false);
    }

  }

  writeIdentifierInForm() {
    if (this.controlForValidation.invalid || this.controlForValidation.pending) {
      // do not write invalid fields into parent form
      this.controlForValidationDisplayActive = true;
      return;
    }

    if ((typeof this.inputFieldValue) === 'string') {
      this.inputFieldValue = this.inputFieldValue.trim();
    }

    if (!this.activeInlineEditingInternal$.getValue() || this.loadingIndicator) {
      return;
    }
    this.readonly$.next(true);
    this.onChange(this.inputFieldValue);
    this.currentValue = this.inputFieldValue;
    if (this.isTextArea) {
      timer(1000).subscribe(() => {
        this.checkForScrollbar();
      });
    }
    this.editModeIsActive.next(false);
  }

  setToReadonly() {
    this.controlForValidationDisplayActive = false;
    this.inputFieldValue = this.currentValue;
    this.readonly$.next(true);
    this.updateControlForValidationMessage();
    this.editModeIsActive.next(false);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public checkForScrollbar() {
    timer(1).subscribe(() => {
      if (this.isTextArea) {
        this.inputElement.nativeElement.style.height = this.inputElement.nativeElement.scrollHeight + 'px';
      }
    });

  }

  public handleOnTouched() {
    this.touch.emit();

    if (this.onTouched) {
      this.onTouched();
    }
  }

  private updateControlForValidationMessage() {
    if (this.control && this.control?.control?.validator) {
      this.controlForValidation.patchValue(this.inputFieldValue);
      this.controlForValidation.setErrors(this.control?.control?.validator(this.controlForValidation));
      const inputElement = this.numericInput ? this.inputElement.numericInput : this.inputElement;
      if (this.controlForValidation.errors) {
        this.controlForValidationDisplayActive = true;
        inputElement.nativeElement.classList.add('invalidControl');
      } else {
        inputElement.nativeElement.classList.remove('invalidControl'); 
      }      
    }
  }
}
